import { useEffect } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqs = [
  {
    question: "Do I have to run?",
    answer:
      "Not at all! You can walk, jog, or run — whatever works for you. It's about having fun, not setting records.",
  },
  {
    question: "Can I participate solo?",
    answer:
      "Yes! You can join solo or with a team. Solo participants are welcome and common.",
  },
  {
    question: "Is it competitive?",
    answer:
      "There are results and rankings if you want them, but most people come for the fun. We celebrate participation, not just winning.",
  },
  {
    question: "Do I need to be good at quizzes?",
    answer:
      "No quiz experience needed. The questions are riddles and logic puzzles — designed to be interesting and fun, not intimidating. You don't need to memorise facts.",
  },
  {
    question: "Does it cost anything?",
    answer:
      "Quizrun runs on community support — it funds bananas, prizes, development, and questions. Registration is required for every event. Your first regular Quizrun is free (special events like trails/hikes are ticketed).",
  },
  {
    question: "Is it online or in-person?",
    answer:
      "We have in-person events in parks and remote events where you track your own run with your phone or smartwatch. None of our events are fully online — you always run!",
  },
  {
    question: "How long does it take?",
    answer:
      "Depends on your speed — both thinking and running! With walkers included it's normally around 1 hour, plus post-event social time.",
  },
  {
    question: "What do I need to bring?",
    answer:
      "Just yourself and a phone! You'll need it to submit answers. Comfortable shoes recommended.",
  },
  {
    question: "What language are the questions in?",
    answer: "Questions are in English, but you can answer in any language.",
  },
];

const JSONLD_ID = "faq-jsonld";

export const FAQSection = () => {
  // Inject FAQPage JSON-LD into <head> only while this component is mounted
  // (homepage only), so schema always matches visible page content.
  useEffect(() => {
    // Remove any stale tag first (guards against HMR/remounts leaving a leftover).
    document.getElementById(JSONLD_ID)?.remove();
    // Drop the hardcoded pre-JS FAQ block so we never ship two FAQPage schemas;
    // this client version matches the visible accordion and supersedes it.
    document.getElementById("faq-jsonld-static")?.remove();
    const script = document.createElement("script");
    script.id = JSONLD_ID;
    script.type = "application/ld+json";
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: { "@type": "Answer", text: f.answer },
      })),
    });
    document.head.appendChild(script);
    return () => {
      script.remove();
    };
  }, []);

  return (
    <section className="py-20 bg-muted/30" id="faq">
      <div className="container mx-auto px-5 md:px-8">
        <div className="text-center mb-12 space-y-3">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
            Frequently Asked Questions
          </h2>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Everything you need to know before your first Quizrun
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq) => (
              <AccordionItem key={faq.question} value={faq.question}>
                <AccordionTrigger className="text-left text-base font-medium hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};
